import { WorkItem } from "@buzzeasy/shared-frontend-utilities";
import { PropsWithChildren, ReactElement, useCallback, useContext, useState } from "react";
import { WorkItemInfo, WorkItemInfoContext } from "./WorkItemInfoContext";

type WorkItemId = WorkItem["workItemId"];

/**
 * Provides extra information for workItems.
 *
 * **No provider dependencies**
 */
export default function WorkItemInfoProvider({ children }: PropsWithChildren): ReactElement {
  const [workItemInfoDict, setWorkItemInfoDict] = useState<Record<WorkItemId, WorkItemInfo | undefined>>({});

  const setInfo = useCallback(
    (wId: WorkItemId, info: Partial<WorkItemInfo> | ((currentInfo: WorkItemInfo | undefined) => Partial<WorkItemInfo>)) => setWorkItemInfoDict(curr => ({
      ...curr,
      [wId]: typeof info === "function" ? info(curr[wId]) : { ...curr[wId], ...info },
    })),
    [],
  );

  const removeInfo = useCallback(
    (wId: WorkItemId) => setWorkItemInfoDict((curr) => {
      const newDict = { ...curr };
      delete newDict[wId];
      return newDict;
    }),
    [],
  );

  return (
    <WorkItemInfoContext.Provider value={{ workItemInfoDict, setInfo, removeInfo }}>
      {children}
    </WorkItemInfoContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useWorkItemInfo(workItemId: WorkItemId): WorkItemInfo | undefined {
  const { workItemInfoDict } = useContext(WorkItemInfoContext);
  return workItemInfoDict[workItemId];
}
